// @ts-strict-ignore
import type { ReactNode } from "react";
import { useState } from "react";

import type { EmersonProductSearchResult } from "@clinic/emerson/data/splinters/EmersonProductSearchResult.fragment.generated";
import type { EmersonProductSearchResultOpen } from "@clinic/emerson/data/splinters/EmersonProductSearchResultOpen.fragment.generated";
import { useGoToProductDetails } from "@clinic/emerson/hooks/useGoToProductDetails/useGoToProductDetails";
import { useEmersonCatalogClearSearch } from "@emerson/hooks/useEmersonCatalogClearSearch/useEmersonCatalogClearSearch";
import { useEmersonCatalogParams } from "@emerson/hooks/useEmersonCatalogParams/useEmersonCatalogParams";
import { CatalogSearchBarWithFilters } from "@shared/components/CatalogSearch/CatalogSearchBarWithFilters/CatalogSearchBarWithFilters";
import { AnalyticsEvent } from "@shared/hooks/useAnalyticsLogger/AnalyticsEvent";
import { SearchApplyEntryPoint } from "@shared/hooks/useAnalyticsLogger/StoreAnalyticsEntryPoint";
import { useProductSearchVariables } from "@shared/hooks/useProductSearchVariables/useProductSearchVariables";
import { useSharedGlobalConfig } from "@shared/hooks/useSharedGlobalConfig/useSharedGlobalConfig";

import { useProductSearchEmersonQuery } from "./ProductSearch.query.generated";

interface Props {
  renderResults?: (
    searchResults: EmersonProductSearchResult[] | EmersonProductSearchResultOpen[],
    pinnedIds: string[],
    totalCount: number,
    isLoadingNewResults: boolean,
    isLoadingMoreResults: boolean,
    handleClearFilters: () => void,
    handleLoadMore?: () => void
  ) => ReactNode;
  hideFilterTags?: boolean;
  hideSearchField?: boolean;
  clearAllPriorSearchParam?: boolean;
}

const ProductSearch = ({
  renderResults,
  hideFilterTags,
  hideSearchField,
  clearAllPriorSearchParam,
}: Props) => {
  const { isUnauthenticated } = useSharedGlobalConfig();
  const [catalogParams, setCatalogParams] = useEmersonCatalogParams();

  const { clearSearchFilters: handleClearFilters } = useEmersonCatalogClearSearch();
  const productSearchVariables = useProductSearchVariables();
  const [isLoadingMoreResults, setIsLoadingMoreResults] = useState(false);
  const hasCatalogParams = Object.keys(catalogParams).length;
  const navigateToPDPForProduct = useGoToProductDetails();
  const {
    data: searchResultData,
    loading: isLoadingNewResults,
    fetchMore,
  } = useProductSearchEmersonQuery({
    variables: {
      isUnauthenticated,
      first: 24,
      ...productSearchVariables,
      filters: {
        onlyIngredient: false,
        ...productSearchVariables?.filters,
      },
    },
    skip: !hasCatalogParams,
  });

  const {
    nodes: searchResults,
    totalCount,
    pageInfo,
    pinnedIds,
  } = (!isLoadingNewResults && searchResultData?.viewer?.productSearchV2) || {};

  const updateQuery = (prev, { fetchMoreResult }) => {
    setIsLoadingMoreResults(false);
    if (!fetchMoreResult) return prev;

    return {
      ...prev,
      viewer: {
        ...prev.viewer,
        productSearchV2: {
          ...fetchMoreResult.viewer.productSearchV2,
          nodes: [
            ...prev.viewer.productSearchV2.nodes,
            ...fetchMoreResult.viewer.productSearchV2.nodes,
          ],
          pageInfo: {
            ...fetchMoreResult.viewer.productSearchV2.pageInfo,
            hasPreviousPage: false,
          },
          pinnedIds,
        },
      },
    };
  };

  const loadMore = () => {
    setIsLoadingMoreResults(true);
    fetchMore({
      variables: {
        after: pageInfo?.endCursor,
      },
      updateQuery,
    });
  };

  const handleLoadMore = pageInfo?.hasNextPage ? loadMore : null;

  const goToProductDetails = (productId: string) => {
    return navigateToPDPForProduct(productId);
  };

  const handleClearQuery = () => {
    if (clearAllPriorSearchParam) {
      setCatalogParams({
        query: undefined,
        typeaheadSelectionId: undefined,
      });
    } else {
      setCatalogParams({
        ...catalogParams,
        query: undefined,
        typeaheadSelectionId: undefined,
      });
    }
  };

  return (
    <>
      <CatalogSearchBarWithFilters
        onClearFilters={handleClearFilters}
        onClearQuery={handleClearQuery}
        goToProductDetails={goToProductDetails}
        hideFilterTags={hideFilterTags}
        hideFilterButton={hideFilterTags}
        hideSearchField={hideSearchField}
        inputSize="normal"
        isMarginless
        isFocused={false}
        entryPointForLogging={SearchApplyEntryPoint.CATALOG_PAGE}
        shouldSyncSearchText
        shouldLogOriginUrl
        eventNameForSearchLogging={
          isUnauthenticated
            ? AnalyticsEvent.EMERSON_UNAUTH_SEARCH_APPLY
            : AnalyticsEvent.EMERSON_SEARCH_APPLY
        }
        clearAllPriorSearchParam={clearAllPriorSearchParam}
      />
      {renderResults?.(
        searchResults,
        pinnedIds,
        totalCount,
        isLoadingNewResults,
        isLoadingMoreResults,
        handleClearFilters,
        handleLoadMore
      )}
    </>
  );
};

export { ProductSearch, type Props };
